/**
 * Creates a promise that rejects after a specified timeout
 * @param timeoutMs Timeout in milliseconds
 * @param logMessagePrefix Optional prefix for timeout error message that will
 * be prepended to the Error thrown on timeout.
 * @returns Promise that rejects with timeout error
 */
export function createTimeoutPromise(
  timeoutMs: number,
  logMessagePrefix?: string,
): Promise<never> {
  return new Promise<never>((_, reject) => {
    setTimeout(() => {
      let errorMessage = `Operation timed out after ${timeoutMs}ms`;
      if (logMessagePrefix) {
        errorMessage = `${logMessagePrefix} ${errorMessage}`;
      }
      reject(new Error(errorMessage));
    }, timeoutMs);
  });
}

/**
 * Waits for promise(s) to resolve or rejects if timeout is reached
 * @param promise Single promise or array of promises to wait for
 * @param timeoutMs Timeout in milliseconds
 * @param logMessagePrefix Optional prefix for timeout error message that will
 * be prepended to the Error thrown on timeout.
 * @returns Promise that resolves with the result(s) or rejects on timeout
 */
export function waitWithTimeout<T>(
  promise: Promise<T> | Promise<T>[],
  timeoutMs: number,
  logMessagePrefix?: string,
): Promise<T | T[]> {
  const timeoutPromise = createTimeoutPromise(timeoutMs, logMessagePrefix);
  const targetPromise = Array.isArray(promise) ? Promise.all(promise) : promise;
  return Promise.race([targetPromise, timeoutPromise]);
}
